
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box, TextField, Typography, Button, IconButton, Container, Paper,
  Card, CardContent, Divider, CircularProgress, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, MenuItem, Chip, Collapse,
  Modal, Fade, Backdrop,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useNavigate } from "react-router-dom";
import { fetchUserFromRedis } from "../AuthonticationPages/Register";
import axios from "axios";
import PackageCard from "../Cards/PackageCard";

const C = {
  primary: "#006D77",
  primaryDark: "#004F55",
  primaryLight: "#E0F7FA",
  bg: "#F7FAFA",
  border: "#EEEEEE",
  text: "#1a1a1a",
  muted: "#999",
  error: "#EF5350",
  success: "#2E7D32",
  successLight: "#E8F5E9",
};

const BASE_URL = process.env.REACT_APP_BASE_DIAGNOSTIC_TESTS;
const LIMIT = 20;

const CATEGORY_META = {
  // ── Primary Categories ──
  "packages":                    { color: "#EFF8EF", border: "#4CAF50", emoji: "🧾" },
  "health packages":            { color: "#EFF8EF", border: "#4CAF50", emoji: "🏥" },
  "anaemia":                    { color: "#FFF0F0", border: "#EF5350", emoji: "🩸" },
  "diabetes":                   { color: "#FFF7EE", border: "#FF9800", emoji: "💉" },
  "heart":                      { color: "#FEF0F4", border: "#E91E63", emoji: "❤️" },
  "liver":                      { color: "#FFFBEE", border: "#FFC107", emoji: "🫁" },
  "lungs":                      { color: "#EEF6FF", border: "#2196F3", emoji: "🫧" },
  "fertility":                  { color: "#F9F0FF", border: "#9C27B0", emoji: "🌱" },
  "kidney":                     { color: "#EEF0FF", border: "#3F51B5", emoji: "🫘" },
  "allergy":                    { color: "#F3FAF0", border: "#8BC34A", emoji: "🌿" },
  "thyroid":                    { color: "#E8FDFF", border: "#00BCD4", emoji: "🦋" },
  "vitamins":                   { color: "#FFFDE7", border: "#F9A825", emoji: "💊" },
  "fever":                      { color: "#FFF0EE", border: "#F44336", emoji: "🌡️" },
  "bone":                       { color: "#F7F7F7", border: "#9E9E9E", emoji: "🦴" },
  "immunity":                   { color: "#EFF8EF", border: "#43A047", emoji: "🛡️" },
  "cancer":                     { color: "#FEF0F4", border: "#E91E63", emoji: "🔬" },
  "hormones":                   { color: "#F3EEFF", border: "#673AB7", emoji: "⚗️" },
  "prostate":                   { color: "#EEF0FF", border: "#3F51B5", emoji: "🩺" },
  "pregnancy":                  { color: "#F9F0FF", border: "#AB47BC", emoji: "🤱" },
  "hiv":                        { color: "#E8FFFD", border: "#009688", emoji: "🧬" },
  "tuberculosis":               { color: "#EEF6FF", border: "#42A5F5", emoji: "🫁" },
  "other":                      { color: "#F5F5F5", border: "#BDBDBD", emoji: "🧪" },
  // ── Extended Categories ──
  "other pathology tests":      { color: "#F5FBE7", border: "#9CCC65", emoji: "🧪" },
  "musculo skeletal disorders": { color: "#ECEFF1", border: "#607D8B", emoji: "🦴" },
  "toxicology":                 { color: "#FFFBEE", border: "#FF8F00", emoji: "⚠️" },
  "clotting disorders":         { color: "#FFF0F0", border: "#EF9A9A", emoji: "🩸" },
  "drug level monitoring":      { color: "#EEF0FF", border: "#5C6BC0", emoji: "💊" },
  "heavy metals":               { color: "#ECEFF1", border: "#78909C", emoji: "⚙️" },
  "infectious disease":         { color: "#EFF8EF", border: "#66BB6A", emoji: "🦠" },
  "blood disorders":            { color: "#FFF0F0", border: "#EF5350", emoji: "🩸" },
  "autoimmune disorders":       { color: "#F9F0FF", border: "#AB47BC", emoji: "🔰" },
  "stomach":                    { color: "#FFF7EE", border: "#FFA726", emoji: "🫃" },
  "inflammatory markers":       { color: "#FFF0EE", border: "#FF7043", emoji: "🔥" },
  "electrolytes":               { color: "#FFFDE7", border: "#FFD54F", emoji: "⚡" },
};

const KEYWORD_RULES = [
  ["thyroid","thyroid"],["diabetes","diabetes"],["diabetic","diabetes"],
  ["heart","heart"],["cardiac","heart"],["lipid","heart"],["cholesterol","heart"],
  ["liver","liver"],["hepat","liver"],["lft","liver"],
  ["kidney","kidney"],["renal","kidney"],
  ["lung","lungs"],["pulmo","lungs"],
  ["cancer","cancer"],["tumour","cancer"],["tumor","cancer"],["marker","cancer"],
  ["anaemia","anaemia"],["anemia","anaemia"],["haemoglob","anaemia"],["hemoglob","anaemia"],
  ["iron","anaemia"],["ferrit","anaemia"],
  ["hormone","hormones"],["hormonal","hormones"],["endocrin","hormones"],
  ["fertility","fertility"],["infertil","fertility"],["semen","fertility"],["sperm","fertility"],
  ["prostate","prostate"],["psa","prostate"],
  ["pregnancy","pregnancy"],["prenatal","pregnancy"],["natal","pregnancy"],["obstetric","pregnancy"],
  ["torch","infectious disease"],["viral","infectious disease"],["virus","infectious disease"],
  ["hiv","hiv"],["aids","hiv"],
  ["tuberculosis","tuberculosis"],
  ["typhoid","fever"],["malaria","fever"],["dengue","fever"],["fever","fever"],["widal","fever"],
  ["allerg","allergy"],
  ["bone","bone"],["calcium","bone"],["mineral","bone"],
  ["vitamin","vitamins"],
  ["electrolyte","electrolytes"],["sodium","electrolytes"],["potassium","electrolytes"],
  ["coagul","clotting disorders"],["clott","clotting disorders"],["platelet","clotting disorders"],
  ["blood","blood disorders"],["cbc","blood disorders"],["haemogram","blood disorders"],
  ["urin","other pathology tests"],["stool","other pathology tests"],["faecal","other pathology tests"],
  ["cultur","infectious disease"],["sensitiv","infectious disease"],["microbi","infectious disease"],
  ["immunolog","immunity"],["autoimmune","autoimmune disorders"],
  ["inflamm","inflammatory markers"],["stomach","stomach"],["gastro","stomach"],
  ["toxicol","toxicology"],["drug level","drug level monitoring"],["heavy metal","heavy metals"],
  ["compreh","health packages"],["complete","health packages"],["full body","health packages"],
  ["senior","health packages"],["wellness","health packages"],["package","health packages"],
  ["profile","health packages"],["panel","health packages"],["screening","health packages"],
];

const DEFAULT_META = { color: "#F5F5F5", border: "#BDBDBD", emoji: "🧪" };

function getCategoryMeta(cat) {
  const key = (cat || "").toLowerCase().trim();
  if (CATEGORY_META[key]) return CATEGORY_META[key];
  for (const [keyword, catKey] of KEYWORD_RULES) {
    if (key.includes(keyword) && CATEGORY_META[catKey]) return CATEGORY_META[catKey];
  }
  return DEFAULT_META;
}

// ─── STEP BAR ────────────────────────────────────────────────────────────────
function StepBar({ step }) {
  const steps = ["Choose Tests", "Review Cart", "Patient & Appointment"];
  return (
    <Box display="flex" alignItems="center" px={1} py={0.5}>
      {steps.map((label, i) => {
        const num = i + 1;
        const done = step > num;
        const active = step === num;
        return (
          <React.Fragment key={num}>
            <Box display="flex" flexDirection="column" alignItems="center" flex={1} px={0.5}>
              <Box sx={{
                width: 30, height: 30, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: done || active ? C.primary : "#E0E0E0",
                border: active ? `3px solid #B2DFDB` : "none",
                transition: "all 0.3s",
              }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: done || active ? "#fff" : "#999" }}>
                  {done ? "✓" : num}
                </Typography>
              </Box>
              <Typography sx={{
                fontSize: 9.5, mt: 0.6, textAlign: "center", lineHeight: 1.3,
                fontWeight: done || active ? 700 : 400,
                color: done || active ? C.primary : "#bbb",
              }}>
                {label}
              </Typography>
            </Box>
            {i < steps.length - 1 && (
              <Box sx={{
                height: 2, flex: 1, mb: 2.5,
                bgcolor: step > num ? C.primary : "#E0E0E0",
                transition: "background-color 0.3s",
              }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// ─── CATEGORY CARD ────────────────────────────────────────────────────────────
function CategoryCard({ name, isActive, onClick }) {
  const meta = getCategoryMeta(name);
  return (
    <Box onClick={onClick} sx={{
      cursor: "pointer",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
      p: 1.2, pt: 1.5, borderRadius: "12px",
      border: isActive ? `2px solid ${meta.border}` : "1.5px solid #EEEEEE",
      backgroundColor: isActive ? meta.color : "#fff",
      minHeight: 85, transition: "all 0.18s",
      boxShadow: isActive ? `0 4px 14px ${meta.border}33` : "0 1px 4px rgba(0,0,0,0.06)",
      "&:hover": { backgroundColor: meta.color, borderColor: meta.border, boxShadow: `0 4px 14px ${meta.border}33`, transform: "translateY(-1px)" },
    }}>
      <Box sx={{
        width: 40, height: 40, borderRadius: "10px",
        backgroundColor: meta.color, border: `1px solid ${meta.border}44`,
        display: "flex", alignItems: "center", justifyContent: "center",
        mb: 0.8, fontSize: 20,
      }}>
        {meta.emoji}
      </Box>
      <Typography sx={{
        fontSize: 9, textAlign: "center", lineHeight: 1.3,
        fontWeight: isActive ? 700 : 500,
        color: isActive ? meta.border : "#444",
        wordBreak: "break-word",
      }}>
        {name}
      </Typography>
    </Box>
  );
}

// ─── TEST CARD ────────────────────────────────────────────────────────────────

function TestCard({ test, quantity, onAdd, onIncrement, onDecrement }) {
  const [showInfo, setShowInfo] = useState(false);
  const meta = getCategoryMeta(test.category);
  const price = test.non_membership || test.proposed_rate || test.mrp || 0;
  const mrp = test.mrp || 0;
  const hasDiscount = mrp > 0 && price < mrp;
  const isInCart = quantity > 0;

  return (
    <Card variant="outlined" sx={{
      borderRadius: "14px",
      border: isInCart ? `2px solid ${C.primary}` : "1.5px solid #EEEEEE",
      boxShadow: isInCart ? `0 4px 16px ${C.primary}25` : "0 1px 6px rgba(0,0,0,0.05)",
      overflow: "hidden", transition: "all 0.2s",
      display: "flex", flexDirection: "column",
      position: "relative",
    }}>
      {isInCart && (
        <Box sx={{
          position: "absolute", top: 8, right: 8, zIndex: 2,
          bgcolor: C.primary, borderRadius: "20px",
          px: 1, py: 0.3, display: "flex", alignItems: "center", gap: 0.4,
        }}>
          <CheckCircleIcon sx={{ fontSize: 11, color: "#fff" }} />
          <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#fff" }}>
            In Cart ({quantity})
          </Typography>
        </Box>
      )}

      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 }, flex: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ p: 1.8, flex: 1 }}>
          <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
            <Box sx={{
              width: 38, height: 38, borderRadius: "10px",
              backgroundColor: meta.color, border: `1px solid ${meta.border}44`,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, fontSize: 19, mt: 0.2,
            }}>
              {meta.emoji}
            </Box>
            <Box flex={1} minWidth={0} pr={isInCart ? 6 : 3}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.4 }}>
                {test.test_name}
              </Typography>
              {test.category && (
                <Typography sx={{ fontSize: 10, color: "#999", mt: 0.2 }}>{test.category}</Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => setShowInfo(v => !v)}
              sx={{ color: showInfo ? C.primary : "#bbb", mt: -0.5, flexShrink: 0, p: 0.3 }}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <Collapse in={showInfo}>
            <Box sx={{
              borderRadius: "10px", border: `1px solid ${meta.border}66`,
              backgroundColor: meta.color, p: 1.2, mb: 1,
            }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: meta.border, mb: 0.5, textTransform: "uppercase", letterSpacing: 0.4 }}>
                ℹ️ About
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: "#444", lineHeight: 1.6 }}>
                {test.description || "No description available."}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.8}>
                {test.parameters && (
                  <Chip label={`${test.parameters} Params`} size="small"
                    sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#fff", border: `1px solid ${meta.border}88`, color: meta.border }} />
                )}
                {test.sample_type && (
                  <Chip label={`${test.sample_type}`} size="small"
                    sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#fff", border: "1px solid #ddd" }} />
                )}
                {test.test_code && (
                  <Chip label={`${test.test_code}`} size="small"
                    sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#fff", border: "1px solid #ddd" }} />
                )}
              </Box>
            </Box>
          </Collapse>

          <Box display="flex" justifyContent="space-between" alignItems="flex-end" mt="auto">
            <Box>
              <Typography sx={{ fontSize: 9.5, color: "#999", mb: 0.2 }}>Our Price</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.primary }}>₹{price.toFixed(2)}</Typography>
            </Box>
            <Box textAlign="right">
              {hasDiscount && (
                <>
                  <Typography sx={{ fontSize: 9.5, color: "#bbb", textDecoration: "line-through" }}>
                    ₹{mrp.toFixed(2)}
                  </Typography>
                  <Box sx={{ bgcolor: "#E8F5E9", borderRadius: "4px", px: 0.7, py: 0.2, display: "inline-block" }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#2E7D32" }}>
                      {Math.round(((mrp - price) / mrp) * 100)}% off
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Add to Cart / Quantity stepper */}
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {quantity > 0 ? (
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              border: `1.5px solid ${C.primary}`, borderRadius: "10px", py: 0.5, px: 1,
            }}>
              <IconButton size="small" onClick={() => onDecrement(test)} sx={{ color: C.primary }}>
                <RemoveIcon size={16} />
              </IconButton>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.primary }}>{quantity}</Typography>
              <IconButton size="small" onClick={() => onIncrement(test)} sx={{ color: C.primary }}>
                <AddIcon size={16} />
              </IconButton>
            </Box>
          ) : (
            <Button fullWidth variant="contained" onClick={() => onAdd(test)} sx={{
              borderRadius: "10px", py: 0.9, fontWeight: 700, fontSize: 12, textTransform: "none",
              backgroundColor: C.primary, boxShadow: "none",
              "&:hover": { backgroundColor: C.primaryDark, boxShadow: "none" },
            }}>
              + Add to Cart
            </Button>
          )}
        </Box>

        <Box sx={{ bgcolor: "#FAFAFA", borderTop: "1px solid #F0F0F0", px: 1.5, py: 0.8 }}>
          <Typography sx={{ fontSize: 9.5, color: "#888", fontWeight: 500 }}>
            🏠 {test.sample_type ? `${test.sample_type} · Home collection` : "Home collection"}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
// ─── PAGINATION ───────────────────────────────────────────────────────────────
function Pagination({ page, totalPages, totalTests, onPageChange }) {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    const end = Math.min(start + 4, totalPages);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: 0.8, mt: 2.5, mb: 1, flexWrap: "wrap",
    }}>
      <IconButton size="small" disabled={page === 1} onClick={() => onPageChange(page - 1)}
        sx={{ border: "1px solid #E0E0E0", borderRadius: "8px", color: page === 1 ? "#ccc" : C.primary, bgcolor: "#fff", width: 32, height: 32, "&:hover": { bgcolor: C.primaryLight } }}>
        <KeyboardArrowLeftIcon sx={{ fontSize: 18 }} />
      </IconButton>

      {getPageNumbers().map(num => (
        <Box key={num} onClick={() => onPageChange(num)} sx={{
          width: 32, height: 32, borderRadius: "8px",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", fontWeight: page === num ? 700 : 400, fontSize: 13,
          bgcolor: page === num ? C.primary : "#fff",
          color: page === num ? "#fff" : "#555",
          border: `1px solid ${page === num ? C.primary : "#E0E0E0"}`,
          transition: "all 0.15s",
          "&:hover": { bgcolor: page === num ? C.primaryDark : C.primaryLight },
        }}>
          {num}
        </Box>
      ))}

      <IconButton size="small" disabled={page === totalPages} onClick={() => onPageChange(page + 1)}
        sx={{ border: "1px solid #E0E0E0", borderRadius: "8px", color: page === totalPages ? "#ccc" : C.primary, bgcolor: "#fff", width: 32, height: 32, "&:hover": { bgcolor: C.primaryLight } }}>
        <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <Typography sx={{ fontSize: 11, color: "#999", ml: 0.5 }}>
        Page {page} of {totalPages} · {totalTests} tests
      </Typography>
    </Box>
  );
}

// ─── CART REVIEW — STEP 2 ─────────────────────────────────────────────────────

function CartReview({ cart, onRemove, onUpdateQuantity, onContinue, onBack }) {
  const total = cart.reduce((s, t) => s + (t.non_membership || t.proposed_rate || t.mrp || 0) * (t.quantity || 1), 0);
  const mrpTotal = cart.reduce((s, t) => s + (t.mrp || 0) * (t.quantity || 1), 0);
  const savings = mrpTotal - total;
   const community_data = "Honer Vivantis"

 const deliveryCharge = community_data!== "Honer Vivantis"
    ? Number(process.env.REACT_APP_DELIVERY_CHARGE) || 0:0;

  if (cart.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Typography sx={{ fontSize: 56 }}>🛒</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#bbb", mt: 2 }}>Your cart is empty</Typography>
        <Typography sx={{ fontSize: 13, color: "#ccc", mt: 0.5, mb: 3 }}>Add some tests to continue</Typography>
        <Button variant="contained" onClick={onBack}
          sx={{ bgcolor: C.primary, textTransform: "none", borderRadius: "10px", px: 3, fontWeight: 700, "&:hover": { bgcolor: C.primaryDark } }}>
          ← Browse Tests
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.text }}>Your Cart</Typography>
          <Typography sx={{ fontSize: 12, color: "#999", mt: 0.3 }}>
            {cart.length} test{cart.length > 1 ? "s" : ""} selected
          </Typography>
        </Box>
        <Button size="small" onClick={onBack} startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 12 }} />}
          sx={{ textTransform: "none", color: C.primary, fontWeight: 600, fontSize: 13, border: `1px solid ${C.primaryLight}`, borderRadius: "8px", px: 1.5 }}>
          Add More
        </Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5, mb: 3 }}>
        {cart.map(test => {
          const meta = getCategoryMeta(test.category);
          const price = test.non_membership || test.proposed_rate || test.mrp || 0;
          const qty = test.quantity || 1;
          return (
            <Box key={test.id || test.test_name} sx={{
              display: "flex", alignItems: "center", gap: 1.5,
              p: 1.5, borderRadius: "12px", bgcolor: "#fff",
              border: `1.5px solid ${C.primary}33`,
              boxShadow: `0 2px 8px ${C.primary}10`,
            }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: "10px",
                backgroundColor: meta.color, border: `1px solid ${meta.border}44`,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, fontSize: 22,
              }}>
                {meta.emoji}
              </Box>

              <Box flex={1} minWidth={0}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.text, lineHeight: 1.4 }}>
                  {test.test_name}
                </Typography>
                <Typography sx={{ fontSize: 10, color: "#999", mt: 0.2 }}>{test.category || "—"}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: C.primary, mt: 0.5 }}>
                  ₹{(price * qty).toFixed(2)}
                </Typography>
              </Box>

              {/* Quantity stepper */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                <IconButton size="small" onClick={() => onUpdateQuantity(test, -1)} sx={{ border: "1px solid #E0E0E0" }}>
                  <RemoveIcon size={14} />
                </IconButton>
                <Typography sx={{ fontWeight: 700, fontSize: 13, minWidth: 18, textAlign: "center" }}>{qty}</Typography>
                <IconButton size="small" onClick={() => onUpdateQuantity(test, 1)} sx={{ border: "1px solid #E0E0E0" }}>
                  <AddIcon size={14} />
                </IconButton>
              </Box>

              <IconButton size="small" onClick={() => onRemove(test)}
                sx={{ color: C.error, bgcolor: "#FFF0F0", borderRadius: "8px", p: 0.7, flexShrink: 0,
                  "&:hover": { bgcolor: "#FFCDD2" } }}>
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ borderRadius: "14px", border: "1px solid #EEEEEE", overflow: "hidden", mb: 3 }}>
        <Box sx={{ bgcolor: C.primary, px: 2.5, py: 1.5 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>💰 Price Summary</Typography>
        </Box>
        <Box sx={{ bgcolor: "#FAFFFE", p: 2.5 }}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography sx={{ fontSize: 13, color: "#666" }}>Total MRP</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>₹{mrpTotal.toFixed(2)}</Typography>
          </Box>
          {savings > 0 && (
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography sx={{ fontSize: 13, color: C.success, fontWeight: 600 }}>🏷 Total Savings</Typography>
              <Typography sx={{ fontSize: 13, color: C.success, fontWeight: 700 }}>−₹{savings.toFixed(2)}</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 ,mt:2}}>
                      <Typography fontSize={13} color="#666">
                        Delivery Charges
                      </Typography>
          
                      <Typography fontSize={13} fontWeight={600}>
                        ₹{deliveryCharge.toFixed(2)}
                      </Typography>
                    </Box>
          <Divider sx={{ my: 1.5 }} />
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ fontSize: 16, fontWeight: 800 }}>Total Payable</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: C.primary }}>₹{total.toFixed(2)}</Typography>
          </Box>
        </Box>
      </Box>

      <Button fullWidth variant="contained" onClick={onContinue} sx={{
        py: 1.8, borderRadius: "12px", bgcolor: C.primary,
        fontSize: 15, fontWeight: 800, textTransform: "none",
        boxShadow: `0 6px 16px ${C.primary}55`,
        "&:hover": { bgcolor: C.primaryDark },
      }}>
        Continue to Book Appointment →
      </Button>
    </Box>
  );
}

// ─── PATIENT & APPOINTMENT — STEP 3 ──────────────────────────────────────────
function PatientBooking({ cart, calcTotal, calcMRP, onBack, onProceed }) {
  const [fetchingUser, setFetchingUser] = useState(false);
  const [patientInfo, setPatientInfo] = useState({ name: "", email: "", phone: "", gender: "", dob: "" });
  const [patientErrors, setPatientErrors] = useState({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [appointmentDateTime, setAppointmentDateTime] = useState("");
  const [tempDate, setTempDate] = useState("");
  const [tempTime12, setTempTime12] = useState("");
  const [tempAmPm, setTempAmPm] = useState("AM");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  const normalizeGender = (genderValue) => {
    if (!genderValue) return "";
    
    const normalized = genderValue.toLowerCase().trim();
    
    // Map variations to standard values
    if (normalized === "male" || normalized === "m") return "male";
    if (normalized === "female" || normalized === "f") return "female";
    if (normalized === "other" || normalized === "others") return "other";
    
    // Return original if it matches exactly
    if (["Male", "Female", "other"].includes(genderValue)) return genderValue;
    
    // Default to original value
    return genderValue;
  };

  useEffect(() => {
    const phoneLength = patientInfo.phone.length;
    if (phoneLength > 0 && phoneLength < 10) {
      if (patientInfo.name || patientInfo.email || patientInfo.gender || patientInfo.dob) {
        setPatientInfo(prev => ({ name: "", email: "", phone: prev.phone, gender: "", dob: "" }));
        setPatientErrors({});
      }
      return;
    }
    if (phoneLength === 10) {
      const timer = setTimeout(async () => {
        setFetchingUser(true);
        try {
          const apiUrl = process.env.REACT_APP_GET_USER_BY_PHONE_API;
          const cleanedPhone = patientInfo.phone.trim().replace(/^(\+91|91)/, "").replace(/[\s-]/g, "");
          const response = await axios.get(`${apiUrl}/${cleanedPhone}`);
          if (response.data?.success && response.data?.user) {
            const d = response.data.user;
            setPatientInfo(prev => ({
              ...prev,
              name: d.name || prev.name,
              email: d.email || prev.email,
              gender:normalizeGender( d.gender) || prev.gender,
              dob: d.age || prev.dob,
            }));
            setDialogMessage("✅ User details loaded successfully!");
            setDialogOpen(true);
            setTimeout(() => setDialogOpen(false), 2000);
          }
        } catch (_) {}
        finally { setFetchingUser(false); }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [patientInfo.phone]);

  const handleChange = (field, value) => {
    setPatientInfo(p => ({ ...p, [field]: value }));
    if (patientErrors[field]) setPatientErrors(e => ({ ...e, [field]: "" }));
  };

  const validate = () => {
    const errors = {};
    if (!patientInfo.name.trim()) errors.name = "Full name is required";
    else if (patientInfo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(patientInfo.email)) errors.email = "Enter a valid email";
    if (!patientInfo.phone.trim()) errors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(patientInfo.phone)) errors.phone = "Enter a valid 10-digit phone";
    if (!patientInfo.gender) errors.gender = "Gender is required";
    setPatientErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getMinDate = () => new Date().toISOString().split("T")[0];
  const getMaxDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 11);
    return d.toISOString().split("T")[0];
  };

  const formatTimeTo24 = (t12, ampm) => {
    const [h, m] = t12.split(":");
    let hour = parseInt(h);
    return `${String(hour).padStart(2, "0")}:${m}`;
  };

  const isTimeInRange = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m >= 360 && h * 60 + m <= 1320;
  };

  const handleDateConfirm = () => {
    if (!tempDate) { setDialogMessage("Please select a date"); setDialogOpen(true); return; }
    const sel = new Date(tempDate);
    const min = new Date(); min.setHours(0, 0, 0, 0);
    const max = new Date(); max.setDate(max.getDate() + 10); max.setHours(23, 59, 59, 999);
    if (sel < min || sel > max) { setDialogMessage("Please select a date within the next 10 days"); setDialogOpen(true); return; }
    setSelectedDate(tempDate);
    setShowDatePicker(false);
    setShowTimePicker(true);
  };

  const handleTimeConfirm = () => {
    if (!tempTime12) { setDialogMessage("Please select a time"); setDialogOpen(true); return; }
    const time24 = formatTimeTo24(tempTime12, tempAmPm);
    if (!isTimeInRange(time24)) { setDialogMessage("Please select a time between 6:00 AM and 10:00 PM"); setDialogOpen(true); return; }
    const [h, m] = time24.split(":").map(Number);
    const dt = new Date(selectedDate); dt.setHours(h, m, 0, 0);
    if (dt <= new Date()) { setDialogMessage("Please select a future date & time"); setDialogOpen(true); return; }
    const tzOffset = -dt.getTimezoneOffset();
    const tzH = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, "0");
    const tzM = String(Math.abs(tzOffset) % 60).padStart(2, "0");
    const tzSign = tzOffset >= 0 ? "+" : "-";
    const iso = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}T${String(dt.getHours()).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")}:00${tzSign}${tzH}:${tzM}`;
    setAppointmentDateTime(iso);
    setShowTimePicker(false);
    setTempTime12(""); setTempAmPm("AM");
  };

  const formattedDateTime = (() => {
    if (!appointmentDateTime) return "Select Date & Time";
    try {
      const p = new Date(appointmentDateTime);
      return isNaN(p) ? "Select Date & Time" : p.toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
      });
    } catch { return "Select Date & Time"; }
  })();

  const handleSubmit = () => {
    if (!validate()) { setDialogMessage("Please fill in all required fields correctly"); setDialogOpen(true); return; }
    if (!appointmentDateTime) { setDialogMessage("Please select appointment date & time"); setDialogOpen(true); return; }
    onProceed({ patientInfo, appointmentDateTime });
  };

  const total = calcTotal();
  const mrpTotal = calcMRP();
  const savings = mrpTotal - total;

  return (
    <Box>
      {/* Cart summary strip */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        bgcolor: "#F0F9FA", border: "1px solid #B2DFDB", borderRadius: "12px", p: 2, mb: 3,
      }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: "10px", bgcolor: C.primaryLight,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 20,
        }}>🧪</Box>
        <Box flex={1} minWidth={0}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
            {cart.length} Test{cart.length > 1 ? "s" : ""} Selected
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#666", mt: 0.2 }} noWrap>
            {cart.slice(0, 2).map(t => t.test_name).join(", ")}{cart.length > 2 ? ` +${cart.length - 2} more` : ""}
          </Typography>
        </Box>
        <Box textAlign="right" flexShrink={0}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: C.primary }}>₹{total.toFixed(2)}</Typography>
          <Button size="small" onClick={onBack} startIcon={<EditIcon sx={{ fontSize: 13 }} />}
            sx={{ fontSize: 11, color: C.primary, textTransform: "none", fontWeight: 600, p: 0, mt: 0.3, minWidth: 0 }}>
            Edit Cart
          </Button>
        </Box>
      </Box>

      <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.primary, mb: 2 }}>Patient Information *</Typography>

      <TextField fullWidth label="Phone Number" value={patientInfo.phone}
        onChange={e => handleChange("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
        error={!!patientErrors.phone}
        helperText={fetchingUser ? "Loading user details..." : patientErrors.phone || "Enter 10-digit phone number"}
        inputProps={{ maxLength: 10 }}
        InputProps={{ endAdornment: fetchingUser ? <CircularProgress size={18} /> : null }}
        sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#FAFAFA" } }}
      />
      <TextField fullWidth label="Full Name" value={patientInfo.name}
        onChange={e => handleChange("name", e.target.value)}
        error={!!patientErrors.name} helperText={patientErrors.name}
        sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#FAFAFA" } }}
      />
      <TextField fullWidth type="email" label="Email" value={patientInfo.email}
        onChange={e => handleChange("email", e.target.value)}
        error={!!patientErrors.email} helperText={patientErrors.email}
        sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#FAFAFA" } }}
      />
      <Box display="flex" gap={2} mb={3}>
        <TextField select fullWidth label="Gender" value={patientInfo.gender}
          onChange={e => handleChange("gender", e.target.value)}
          error={!!patientErrors.gender} helperText={patientErrors.gender}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#FAFAFA" } }}>
          <MenuItem value="">Select Gender</MenuItem>
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
          <MenuItem value="other">Other</MenuItem>
        </TextField>
        <TextField type="number" fullWidth label="Age" value={patientInfo.dob}
          onChange={e => { let v = e.target.value.replace(/\D/g, ""); if (v.length > 3) v = v.slice(0, 3); handleChange("dob", v); }}
          error={!!patientErrors.dob} helperText={patientErrors.dob}
          InputLabelProps={{ shrink: true }}
          InputProps={{ inputProps: { min: 0, max: 200 } }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#FAFAFA" } }}
        />
      </Box>

      <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.primary, mb: 1.5 }}>Appointment Date & Time *</Typography>
      <Box onClick={() => setShowDatePicker(true)} sx={{
        p: 2, cursor: "pointer", border: "1px solid #E0E0E0", borderRadius: 2,
        bgcolor: "#FAFAFA", display: "flex", alignItems: "center", gap: 1.5, mb: 1,
        transition: "all 0.2s", "&:hover": { bgcolor: "#F5F5F5", borderColor: C.primary },
      }}>
        <CalendarTodayIcon sx={{ color: appointmentDateTime ? C.primary : "#999", fontSize: 20 }} />
        <Typography sx={{ fontSize: 14, color: appointmentDateTime ? C.primary : "#999", fontWeight: appointmentDateTime ? 500 : 400 }}>
          {formattedDateTime}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: "text.secondary", ml: 0.5, mb: 3 }}>
        Available: 10+ days from today, 6:00 AM – 10:00 PM
      </Typography>

      {savings > 0 && (
        <Box sx={{ bgcolor: C.successLight, borderRadius: "10px", border: "1px solid #A5D6A7", p: 2, mb: 3 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.success }}>
            🏷 You Save ₹{savings.toFixed(2)} on this order!
          </Typography>
        </Box>
      )}

      <Box sx={{ bgcolor: "#FFF9E6", p: 2.5, borderRadius: "14px", border: "1px solid #FFE082", mb: 3 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#856404", mb: 1.5 }}>📋 What's Next?</Typography>
        <Typography sx={{ fontSize: 13, color: "#856404", mb: 0.8 }}>• Enter your delivery address</Typography>
        <Typography sx={{ fontSize: 13, color: "#856404", mb: 0.8 }}>• Choose online or offline payment</Typography>
        <Typography sx={{ fontSize: 13, color: "#856404" }}>• Our phlebotomist will collect samples at your doorstep</Typography>
      </Box>

      <Button fullWidth variant="contained" onClick={handleSubmit} sx={{
        py: 1.8, borderRadius: "12px", bgcolor: C.primary,
        fontSize: 15, fontWeight: 800, textTransform: "none",
        boxShadow: `0 6px 16px ${C.primary}55`,
        "&:hover": { bgcolor: C.primaryDark },
      }}>
        Proceed to Address · ₹{total.toFixed(2)}
      </Button>

      {/* Date Dialog */}
      <Dialog open={showDatePicker} onClose={() => setShowDatePicker(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Select Date</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>Available up to 10 days from today</Typography>
          <TextField type="date" fullWidth value={tempDate} onChange={e => setTempDate(e.target.value)}
            inputProps={{ min: getMinDate(), max: getMaxDate() }}
            sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setShowDatePicker(false)} sx={{ textTransform: "none", color: "#666" }}>Cancel</Button>
          <Button onClick={handleDateConfirm} variant="contained" sx={{ bgcolor: C.primary, textTransform: "none", px: 3, "&:hover": { bgcolor: C.primaryDark } }}>Next</Button>
        </DialogActions>
      </Dialog>

      {/* Time Dialog */}
      <Dialog open={showTimePicker} onClose={() => { setShowTimePicker(false); setTempTime12(""); setTempAmPm("AM"); }}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Select Time</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>Service available: 6:00 AM – 10:00 PM</Typography>
          <Box display="flex" gap={2} mt={1}>
            <TextField type="time" fullWidth label="Time" value={tempTime12}
              onChange={e => setTempTime12(e.target.value)} inputProps={{ step: 300 }}
              sx={{ flex: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => { setShowTimePicker(false); setTempTime12(""); setTempAmPm("AM"); }} sx={{ textTransform: "none", color: "#666" }}>Cancel</Button>
          <Button onClick={handleTimeConfirm} variant="contained" sx={{ bgcolor: C.primary, textTransform: "none", px: 3, "&:hover": { bgcolor: C.primaryDark } }}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3, minWidth: 280 } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontSize: 15 }}>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} variant="contained" fullWidth
            sx={{ bgcolor: C.primary, textTransform: "none", "&:hover": { bgcolor: C.primaryDark } }}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const DiagnosticSampleBooking = ({ embedded = false, serviceData }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [showAllCategoriesModal, setShowAllCategoriesModal] = useState(false);
  const [step, setStep] = useState(1);
  const topRef = useRef(null);
  const [loading,setLoading]=useState(false)
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [tests, setTests] = useState([]);
  const [testsLoading, setTestsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTests, setTotalTests] = useState(0);
  const [packages, setPackages] = useState([]);
  const [showAllPackagesModal, setShowAllPackagesModal] = useState(false);
  const [searchPackages,setSearchPackages]=useState([])
  const COLS = 6;
  const API = process.env.REACT_APP_API_BASE_URL;


  // ── Fetch categories once ──
  useEffect(() => {
    const fetchCategories = async () => {
      setCatLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/diagnostic-categories`);
            const updatedCategories = [
      { name: "Packages" },
      ...res.data.categories
    ];
     setCategories(updatedCategories);
      } catch {
        setError("Failed to load categories.");
      } finally {
        setCatLoading(false);
      }
    };
    fetchCategories();
  }, []);


const packagesFetchedRef = useRef(false);
useEffect(() => {
  if (packagesFetchedRef.current) return;
  packagesFetchedRef.current = true;
  getAvailablePackages();
}, []); 

  // ── Fetch tests ──
  const fetchTests = useCallback(async (pageNum, cat, searchVal) => {
    setTestsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: pageNum, limit: LIMIT });
      if (searchVal && searchVal.trim()) params.append("search", searchVal.trim());
      if (cat) params.append("category", cat);
      const res = await axios.get(`${BASE_URL}/diagnostic-tests?${params}`);
      setTests(res.data.tests || []);
      setTotalPages(res.data.total_pages || 1);
      setTotalTests(res.data.total || 0);
      setSearchPackages(res.data.packages||[])
    } catch {
      setError("Failed to load diagnostic tests. Please try again.");
    } finally {
      setTestsLoading(false);
    }
  }, []);

  // Ref to prevent double-fire on mount
const filterFiredRef = useRef(false);
const prevPageRef    = useRef(1);

useEffect(() => {
  const delay = search ? 400 : 0;
  const timer = setTimeout(() => {
    filterFiredRef.current = true;
    prevPageRef.current = 1;
    setPage(1);
    fetchTests(1, activeCategory, search);
  }, delay);
  return () => clearTimeout(timer);
}, [search, activeCategory, fetchTests]);

useEffect(() => {
  if (filterFiredRef.current) { filterFiredRef.current = false; return; }
  if (page === prevPageRef.current) return;
  prevPageRef.current = page;
  fetchTests(page, activeCategory, search);
}, [page]);



  const handlePageChange = (newPage) => {
    setPage(newPage);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Cart helpers — use test_name as fallback key if id missing ──
 
  const getKey = (t) => t.id || t.test_name;
const cartIds = useMemo(() => new Set(cart.map(getKey)), [cart]);
const addToCart = (test) => {
  if (!cartIds.has(getKey(test))) setCart(p => [...p, test]);
};
const removeFromCart = (test) => setCart(p => p.filter(t => getKey(t) !== getKey(test)));
// const calcTotal = () => cart.reduce((s, t) => s + (t.non_membership || t.proposed_rate || t.mrp || 0), 0);
const calcTotal = () =>
  cart.reduce((s, t) => s + (t.non_membership || t.proposed_rate || t.mrp || 0) * (t.quantity || 1), 0);

const calcMRP = () => cart.reduce((s, t) => s + (t.mrp || 0), 0);

const updateCartQuantity = (test, delta) => {
  const key = getKey(test);
  setCart((prev) =>
    prev
      .map((t) => (getKey(t) === key ? { ...t, quantity: (t.quantity || 1) + delta } : t))
      .filter((t) => t.quantity > 0) 
  );
};
const cartQtyMap = useMemo(() => {
  const map = new Map();
  cart.forEach((t) => map.set(getKey(t), t.quantity || 1));
  return map;
}, [cart]);


const calcTotalQty = () =>
  cart.reduce((s, t) => s + (t.quantity || 1), 0); 

  const goToStep = (s) => {
    setStep(s);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    if (step > 1) { goToStep(step - 1); return; }
    if (!embedded) navigate(-1);
  };

  const handleProceed = ({ patientInfo, appointmentDateTime }) => {
    const total = calcTotal();
    const mrpTotal = calcMRP();
    const diagnosticOrderData = {
      tests: cart.map(t => ({
        test_id: t.id, test_code: t.test_code, test_name: t.test_name,
        mrp: t.mrp, price: t.non_membership || t.mrp,
        proposed_rate: t.proposed_rate || t.mrp, category: t.category,
        quantity: t.quantity || 1,
      })),
      total_amount: total, mrp_total: mrpTotal, savings: mrpTotal - total,
      appointmentDateTime, patientInfo,
    };
    sessionStorage.setItem("diagnosticOrderData", JSON.stringify(diagnosticOrderData));
    sessionStorage.setItem("selectedTests", JSON.stringify(cart));
    sessionStorage.setItem("patientInfo", JSON.stringify(patientInfo));
    navigate("/medicine-delivery-address", {
      state: {
        orderType: "diagnostic", diagnosticOrderData,
        serviceTitle: "DIAGNOSTIC SAMPLE COLLECTION",
        servicePrice: total, totalAmount: total,
        appointmentDateTime, selectedDate: appointmentDateTime,
        cart, patientInfo,
      },
    });
  };
const getAvailablePackages=async()=>{
  setLoading(true);
      try {
        const res  = await fetch(`${API}/health-camp/packages`);
        const data = await res.json();
        if (data.success) setPackages(data.packages || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }



  return (
    <Box sx={{ minHeight: embedded ? "auto" : "100vh", bgcolor: C.bg }}>
      <Container maxWidth={embedded ? false : "md"} disableGutters={embedded} sx={{ py: embedded ? 0 : 3 }}>
        <Paper elevation={0} sx={{
          borderRadius: embedded ? 0 : 3,
          border: embedded ? "none" : "1px solid #E0E0E0",
          overflow: "hidden", boxShadow: "none",
        }}>

          {/* ── HEADER (standalone only) ── */}
          {!embedded && (
            <Box sx={{ p: 2.5, pb: 2, bgcolor: "#fff", borderBottom: "1px solid #EEE" }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <IconButton onClick={handleBack} sx={{ bgcolor: "#F5F5F5", borderRadius: "10px", width: 36, height: 36 }}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <Box flex={1}>
                  <Typography sx={{ fontSize: 19, fontWeight: 800, color: C.primary }}>
                    Diagnostic Sample Collection
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.2 }}>
                    Home collection · Results in 24–48 hrs
                  </Typography>
                </Box>
                {step === 1 && cart.length > 0 && (
                  <Box onClick={() => goToStep(2)} sx={{
                    position: "relative", cursor: "pointer",
                    bgcolor: C.primaryLight, borderRadius: "10px",
                    width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
                    "&:hover": { bgcolor: "#B2EBF2" },
                  }}>
                    <ShoppingCartIcon sx={{ color: C.primary, fontSize: 20 }} />
                    <Box sx={{
                      position: "absolute", top: -4, right: -4, bgcolor: C.error,
                      borderRadius: "50%", width: 18, height: 18,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Typography sx={{ color: "#fff", fontSize: 9, fontWeight: 700 }}>{cart.length}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          <Box ref={topRef} sx={{ p: embedded ? 1.5 : 3 }}>

            {/* ── STEP BAR ── */}
            <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid #EEE", p: 2, mb: 2.5, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <StepBar step={step} />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* ════════════ STEP 1: BROWSE ════════════ */}
            {step === 1 && (
              <>
                {/* Search */}
                <Box sx={{ position: "sticky", top: 0, zIndex: 10, bgcolor: C.bg, pb: 1.5, pt: 0.5, mb: 1 }}>
                  <Box sx={{
                    display: "flex", alignItems: "center", bgcolor: "#fff",
                    borderRadius: "12px", border: "1px solid #E0E0E0",
                    px: 2, py: 1, gap: 1, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}>
                    <SearchIcon sx={{ color: C.primary, fontSize: 20 }} />
                    <input
                      value={search}
                      onChange={e => { setSearch(e.target.value); if (e.target.value) setActiveCategory(null); }}
                      placeholder="Search tests by name or code..."
                      style={{
                        flex: 1, border: "none", outline: "none",
                        fontSize: 14, color: "#1a1a1a",
                        backgroundColor: "transparent", fontFamily: "inherit",
                      }}
                    />
                    {search && (
                      <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.3 }}>
                        <ClearIcon sx={{ fontSize: 16, color: "#999" }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                {/* Categories */}
                {!search && (
                  <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid #EEE", p: 2, mb: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a" }}>Condition Based Tests</Typography>
                      <Box display="flex" gap={1}>
                        {activeCategory && (
                          <Chip label="Clear ✕" size="small"
                            onClick={() => { setActiveCategory(null); setPage(1); }}
                            sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: C.primaryLight, color: C.primary, border: "1px solid #B2DFDB", cursor: "pointer" }}
                          />
                        )}
                        <Chip
                          label={`📂 ${activeCategory || "Browse All"}`} size="small"
                          onClick={() => setShowAllCategoriesModal(true)}
                          icon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                          sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: "#F5F5F5", cursor: "pointer", "& .MuiChip-icon": { order: 1, mr: "4px !important", ml: "-4px !important" } }}
                        />
                      </Box>
                    </Box>

                    {activeCategory !== "Packages" && (
                      <Box sx={{
                        display: "flex", alignItems: "center", gap: 1,
                        bgcolor: getCategoryMeta(activeCategory).color,
                        border: `1px solid ${getCategoryMeta(activeCategory).border}`,
                        borderRadius: "10px", px: 1.5, py: 1, mb: 1.5,
                      }}>
                        <Typography sx={{ fontSize: 18 }}>{getCategoryMeta(activeCategory).emoji}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: getCategoryMeta(activeCategory).border, flex: 1 }}>
                          📂 {activeCategory}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#888" }}>{totalTests} tests</Typography>
                      </Box>
                    )}

                    {catLoading ? (
                      <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={24} sx={{ color: C.primary }} />
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${COLS}, 1fr)`, gap: 0.8 }}>
                          {categories.slice(0, COLS * 2).map(({ name }) => (
                            <CategoryCard key={name} name={name} isActive={activeCategory === name}
                              onClick={() => { setActiveCategory(activeCategory === name ? null : name); setPage(1); }}
                            />
                          ))}
                        </Box>
                        {categories.length > COLS * 2 && (
                          <Button onClick={() => setShowAllCategoriesModal(true)}
                            sx={{ mt: 1.5, textTransform: "none", color: C.primary, fontSize: 13, fontWeight: 600, width: "100%" }}>
                            Show all {categories.length} categories →
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                )}

                {/* ── Tests 4-column grid ── */}
                <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid #EEE", p: 2, mb: cart.length > 0 ? 10 : 2, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#1a1a1a", flex: 1, mr: 1 }} noWrap>
                      {activeCategory || (search ? `"${search}"` : "All Tests")}
                    </Typography>
                     {activeCategory !== "Packages" && (<Typography sx={{ fontSize: 12, color: "#999", fontWeight: 500, flexShrink: 0 }}>
                      {totalTests} Items
                    </Typography>)}
                  </Box>
                  

                  {
                    activeCategory === "Packages" ? (
  <>
    {packages.length === 0 ? (
      <Box textAlign="center" py={6}>
        <Typography sx={{ fontSize: 48 }}>📦</Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#bbb", mt: 1.5 }}>
          No packages available
        </Typography>
      </Box>
    ) : (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2,1fr)",
            md: "repeat(3,1fr)",
            lg: "repeat(4,1fr)",
          },
          gap: 1.5,
        }}
      >
        {packages.map((pkg) => (
          <Box key={pkg.id} sx={{ minWidth: 300, flexShrink: 0, }} > <PackageCard key={pkg.id} pkg={pkg} onBook={addToCart} onRemove={removeFromCart} isInCart={cartIds.has(getKey(pkg))}/> </Box>
        ))}
      </Box>
    )}
  </>
) :
                  testsLoading ? (
                    <Box display="flex" justifyContent="center" py={6}>
                      <CircularProgress sx={{ color: C.primary }} />
                    </Box>
                  ) : tests.length === 0 ? (
                    <Box textAlign="center" py={6}>
                      <Typography sx={{ fontSize: 48 }}>🧪</Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#bbb", mt: 1.5 }}>No tests found</Typography>
                      <Typography sx={{ fontSize: 12, color: "#ccc", mt: 0.5 }}>Try a different search or category</Typography>
                    </Box>
                  ) : (
                    <Box sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                        lg: "repeat(4, 1fr)",
                      },
                      gap: 1.5,
                    }}>
                      
        {searchPackages.map((pkg) => (
          (page==1&&(<Box key={pkg.id} sx={{ minWidth: 300, flexShrink: 0, }} > <PackageCard key={pkg.id} pkg={pkg} onBook={addToCart} onRemove={removeFromCart} isInCart={cartIds.has(getKey(pkg))}/> </Box>))
        ))}
                      {tests.map(test => (
                        <TestCard
                          key={getKey(test)}
                          test={test}
                          quantity={cartQtyMap.get(getKey(test)) || 0}
                          onAdd={addToCart}
                          onIncrement={(t) => updateCartQuantity(t, 1)}
                          onDecrement={(t) => updateCartQuantity(t, -1)}
                        />
                      ))}
                    </Box>
                  )}

                  {!testsLoading && (
                    <Pagination
                      page={page}
                      totalPages={totalPages}
                      totalTests={totalTests}
                      onPageChange={handlePageChange}
                    />
                  )}
                </Box>
              </>
            )}

            {/* ════════════ STEP 2: CART ════════════ */}
            {step === 2 && (
              <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid #EEE", p: 2, mb: 2, pb: cart.length > 0 ? 10 : 2, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>

                <CartReview
                  cart={cart}
                  onBack={() => goToStep(1)}
                  onRemove={(test) => {
                    removeFromCart(test);
                    if (cart.length <= 1) goToStep(1);
                  }}
                  onUpdateQuantity={updateCartQuantity}
                  onContinue={() => goToStep(3)}
                />
              </Box>
            )}

            {/* ════════════ STEP 3: PATIENT ════════════ */}
            {step === 3 && (
              <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid #EEE", p: 2.5, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                <PatientBooking
                  cart={cart}
                  calcTotal={calcTotal}
                  calcMRP={calcMRP}
                  onBack={() => goToStep(2)}
                  onProceed={handleProceed}
                />
              </Box>
            )}

          </Box>
        </Paper>
      </Container>

{/* ── FLOATING CART BAR — standalone mode ── */}
{!embedded && step === 1 && cart.length > 0 && (
  <Box sx={{
    position: "fixed", bottom: 0, left: 0, right: 0,
    bgcolor: C.primaryDark,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    px: 3, py: 1.5, zIndex: 1300,
    boxShadow: `0 -4px 20px ${C.primary}55`,
  }}>
    <Box display="flex" alignItems="center" gap={1.5}>
      <Box sx={{ position: "relative" }}>
        <Typography sx={{ fontSize: 26 }}>🛒</Typography>
        <Box sx={{ position: "absolute", top: -4, right: -6, bgcolor: C.error, borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "#fff", fontSize: 9, fontWeight: 700 }}>{calcTotalQty()}</Typography>
        </Box>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>
          {calcTotalQty()} test{calcTotalQty() > 1 ? "s" : ""} in cart
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>
          ₹{calcTotal().toFixed(2)}
        </Typography>
      </Box>
    </Box>
    <Button onClick={() => goToStep(2)} sx={{
      bgcolor: "#fff", color: C.primary, fontWeight: 800, fontSize: 18,
      textTransform: "none", borderRadius: "10px", px: 2.5, py: 1.2, minHeight: 40,
      "&:hover": { bgcolor: C.primaryLight },
    }}>
      <ShoppingCartIcon sx={{ color: C.primary, fontSize: 20, marginRight: 1 }} />
      View Cart →
    </Button>
  </Box>
)}

      {/* ── FIXED CART BAR — embedded mode ── */}
      {embedded && step === 1 && cart.length > 0 && (
        <Box sx={{
          position: "fixed", bottom: 0, left: "280px", right: 0, zIndex: 1300,
          bgcolor: C.primaryDark,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 3, py: 1.5,
          boxShadow: `0 -4px 20px ${C.primary}55`,
        }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{ position: "relative" }}>
              <Typography sx={{ fontSize: 22 }}>🛒</Typography>
              <Box sx={{ position: "absolute", top: -4, right: -6, bgcolor: C.error, borderRadius: "50%", width: 17, height: 17, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ color: "#fff", fontSize: 9, fontWeight: 700 }}>{calcTotalQty()}</Typography>
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>
                {calcTotalQty()} test{calcTotalQty() > 1 ? "s" : ""} in cart
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#fff" }}>₹{calcTotal().toFixed(2)}</Typography>
            </Box>
          </Box>
          <Button onClick={() => goToStep(2)} sx={{
            bgcolor: "#fff", color: C.primary, fontWeight: 800, fontSize: 18,
            textTransform: "none", borderRadius: "10px", px: 2.5, py: 1, minHeight: 48,
            "&:hover": { bgcolor: C.primaryLight },
          }}>
            <ShoppingCartIcon sx={{ color: C.primary, fontSize: 20, marginRight: 1 }} />
            View Cart →
          </Button>
        </Box>
      )}

      {/* ── ALL CATEGORIES MODAL ── */}
      <Modal
        open={showAllCategoriesModal}
        onClose={() => setShowAllCategoriesModal(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300 } }}
      >
        <Fade in={showAllCategoriesModal}>
          <Box sx={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff", borderRadius: 3,
            width: { xs: "92%", sm: "80%", md: "65%" },
            maxHeight: "80vh", overflow: "hidden",
            display: "flex", flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          }}>
            <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #F0F0F0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800 }}>All Categories</Typography>
              <IconButton onClick={() => setShowAllCategoriesModal(false)} size="small" sx={{ bgcolor: "#F5F5F5", borderRadius: "50%" }}>
                <ClearIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Box sx={{ overflowY: "auto", p: 2, display: "grid", gridTemplateColumns: `repeat(${COLS}, 1fr)`, gap: 0.8 }}>
              {categories.map(({ name }) => (
                <CategoryCard key={name} name={name} isActive={activeCategory === name}
                  onClick={() => {
                    setActiveCategory(activeCategory === name ? null : name);
                    setPage(1);
                    setShowAllCategoriesModal(false);
                  }}
                />
              ))}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default DiagnosticSampleBooking;